import React, { Dispatch, useEffect, useState } from 'react';

import { Badge, Box, Button, IconButton, MenuItem, Popover, Stack, TextField, Tooltip } from '@mui/material';
import Iconify from 'src/components/iconify';
import { GridToolbarColumnsButton, GridToolbarDensitySelector } from '@mui/x-data-grid';
import { useMyDataStore } from 'src/store/me/me.store';
import { LpEventsRepository } from 'src/repositories/benefits/lp-events.repository';
import { enqueueSnackbar } from 'notistack';
import { AxiosHttpClient } from 'src/http';
import { BENEFITS_URL } from 'src/config-global';

export interface LpProductTableFilterProps {
  searchText: string;
  setSearchText: (value: string) => void;
  setAppliedFilters: Dispatch<any>;
}

export default function LpProductTableFilter({
  searchText,
  setSearchText,
  setAppliedFilters,
}: LpProductTableFilterProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [localfilters, setLocalFilters] = useState<any>({})
  const [filters, setFilters] = useState<any>()
  const openFilters = Boolean(anchorEl)

  const api = new AxiosHttpClient(BENEFITS_URL)
  const lpEventsRepository = new LpEventsRepository(api)

  const roles = useMyDataStore((state) => state.me?.roles);

  const handleOpenFilters = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }

  const handleChange = (field: 'bu' | 'event', value: string) => {
    if (field === 'bu') {
      setLocalFilters({ bu: localfilters.bu, event: undefined })
    }
    setLocalFilters((prev: any) => ({ ...prev, [field]: value }))
  };

  const handleCloseFilters = () => {
    setAnchorEl(null);
    setAppliedFilters(localfilters);
  }

  const handleRemove = (field: 'bu' | 'event') => {
    if (field === 'bu') {
      setLocalFilters({})
      setFilters({ bu: filters.bu, event: [] })
    } else {
      setLocalFilters((prev: any) => {
        const newFilters = { ...prev };
        delete newFilters[field];
        return newFilters;
      });
    }
  };

  useEffect(() => {
    const getBUFilters = async () => {
      if (roles?.some((role) => ['cn_admin', 'lp_promoter'].includes(role))) {
        const BUs = await lpEventsRepository.getBusinessUnits()
        if (BUs.isLeft()) {
          enqueueSnackbar({ message: 'Ocorreu um erro ao carregar os filtros', variant: 'error' })
        } else {
          setFilters({ bu: BUs.value.map((bu: any) => ({ label: bu.name, value: bu._id })), event: [] })
        }
      }
    }

    getBUFilters()
  }, [])

  useEffect(() => {
    const getEventFilters = async () => {
      if (localfilters?.bu) {
        const events = await lpEventsRepository.getLpEventsByBusinessUnit(localfilters.bu)
        if (events.isLeft()) {
          enqueueSnackbar({ message: 'Ocorreu um erro ao carregar os filtros', variant: 'error' })
        } else {
          setFilters({ bu: filters.bu, event: events.value.map((event: any) => ({ label: event.name, value: event._id })) })
        }
      }
    }

    getEventFilters()
  }, [localfilters.bu])

  return (
    <Stack direction='row' justifyContent='space-between' width='100%' data-testid='lpProductTableFilter-render-test'>
      <Stack direction='row' alignItems='center' spacing={1}>
        <TextField
          variant='standard'
          placeholder='Pesquisar...'
          size='small'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <Stack px={0.7} alignItems='center' justifyContent='center'>
                <Iconify icon='material-symbols:search' width={20} />
              </Stack>
            ),
          }}
        />
        <Tooltip
          title={
            <>Somente o campo NOME será filtrado na pesquisa</>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Iconify icon="mdi:information-outline" fontSize={18} />
          </Box>
        </Tooltip>
      </Stack>
      <Stack direction='row' alignItems='center' spacing={1}>
        {
          roles?.some((role) => ['cn_admin', 'lp_promoter'].includes(role)) &&
          <Button onClick={handleOpenFilters} size='small'>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Badge invisible={Object.keys(localfilters).length === 0} badgeContent=""
                sx={{
                  '& .MuiBadge-badge': {
                    minWidth: 10,
                    height: 12,
                  },
                }}
              >
                <Iconify icon='mdi:filter-variant' />
              </Badge>
              Filtros
            </Stack>
          </Button>
        }
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
      </Stack>
      <Popover
        open={openFilters}
        anchorEl={anchorEl}
        onClose={handleCloseFilters}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack p={1} spacing={1} width={250}>
          {
            filters?.bu?.length > 0 &&
            <Stack direction='row' spacing={1} alignItems='center' flex={1}>
              <IconButton sx={{ height: 24, width: 24 }} size='small' onClick={() => handleRemove('bu')}><Iconify icon='mdi:close' /></IconButton>
              <TextField
                select
                fullWidth
                size='small'
                label='BU'
                value={localfilters?.bu ?? ''}
                onChange={(e) => handleChange('bu', e.target.value)}
              >
                {filters?.bu?.map((f: any) => (
                  <MenuItem key={f.value} value={f.value}>
                    {f.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          }
          {
            filters?.event?.length > 0 &&
            <Stack direction='row' spacing={1} alignItems='center'>
              <IconButton sx={{ height: 24, width: 24 }} size='small' onClick={() => handleRemove('event')}><Iconify icon='mdi:close' /></IconButton>
              <TextField
                select
                fullWidth
                size='small'
                label='Evento'
                value={localfilters?.event ?? ''}
                onChange={(e) => handleChange('event', e.target.value)}
              >
                {filters?.event?.map((f: any) => (
                  <MenuItem key={f.value} value={f.value}>
                    {f.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          }
        </Stack>
      </Popover>
    </Stack>
  )
}
